import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const getToken = async (user: any): Promise<string> => {
  const local = localStorage.getItem('accessToken');
  if (local) return local;
  if (user?.getIdToken) return user.getIdToken();
  return '';
};

// ── Types ──────────────────────────────────────────────────────────
interface CognitiveProfile {
  id: string;
  version: number;
  identityIntro: string | null;
  commTone: string | null;
  toneIntensity: number;
  financialThreshold: number | null;
  nonNegotiable: string | null;
  autonomyMode: string;
  alignmentScore: number;
  alignmentTier: string;
  status: string;
}

interface AlignmentResult {
  score: number;
  tier: string;
  breakdown: { layer1: number; learning: number; brainGraph: number };
}

interface AutonomyLimits {
  maxAutonomy: string;
  writeActionsAllowed: boolean;
  requiresApprovalForAll: boolean;
  financialLimit: number;
  description: string;
}

interface Props {
  replicaId: string;
  replicaName?: string;
  onComplete?: () => void;
  onBack?: () => void;
}

// ── Constants ─────────────────────────────────────────────────────
const COMM_TONES = [
  { value: 'analytical', label: 'Analytical', icon: '📊', desc: 'Data-driven, precise, structured' },
  { value: 'diplomatic', label: 'Diplomatic', icon: '🤝', desc: 'Tactful, balanced, relationship-focused' },
  { value: 'direct', label: 'Direct', icon: '⚡', desc: 'Clear, concise, action-oriented' },
  { value: 'visionary', label: 'Visionary', icon: '🔮', desc: 'Big-picture, inspiring, strategic' },
];

const AUTONOMY_MODES = [
  { value: 'advisory', label: 'Advisory', icon: '💬', desc: 'Suggests — never acts. You decide everything.' },
  { value: 'confirmation', label: 'Confirmation', icon: '✅', desc: 'Prepares drafts — sends only after your OK.' },
  { value: 'semi_autonomous', label: 'Semi-Autonomous', icon: '🔄', desc: 'Handles routine tasks. Escalates anything unusual.' },
  { value: 'autonomous', label: 'Autonomous', icon: '🚀', desc: 'Full autonomy within your defined boundaries.' },
];

const SETUP_STEPS = [
  { key: 'intro', title: 'Identity Introduction', subtitle: 'How should your Replica introduce itself?' },
  { key: 'tone', title: 'Communication Tone', subtitle: 'How does your Replica speak?' },
  { key: 'threshold', title: 'Financial Threshold', subtitle: 'When should the Guardian step in?' },
  { key: 'boundary', title: 'Non-Negotiable Rule', subtitle: 'What must your Replica never do?' },
  { key: 'autonomy', title: 'Autonomy Mode', subtitle: 'How much freedom does your Replica get?' },
];

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// ── Component ─────────────────────────────────────────────────────
export const SovereignCoreSetup: React.FC<Props> = ({
  replicaId,
  replicaName = 'your Replica',
  onComplete,
  onBack,
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [existingProfile, setExistingProfile] = useState<CognitiveProfile | null>(null);
  const [alignment, setAlignment] = useState<AlignmentResult | null>(null);
  const [autonomyLimits, setAutonomyLimits] = useState<AutonomyLimits | null>(null);
  const [showComplete, setShowComplete] = useState(false);

  // Form state
  const [identityIntro, setIdentityIntro] = useState('');
  const [commTone, setCommTone] = useState('');
  const [toneIntensity, setToneIntensity] = useState(0.5);
  const [financialThreshold, setFinancialThreshold] = useState('');
  const [nonNegotiable, setNonNegotiable] = useState('');
  const [autonomyMode, setAutonomyMode] = useState('advisory');

  // ── Load existing profile ──────────────────────────────────────
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const authToken = await getToken(user);
        const res = await fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/profile`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json();
        if (data.success && data.profile) {
          setExistingProfile(data.profile);
          setIdentityIntro(data.profile.identityIntro || '');
          setCommTone(data.profile.commTone || '');
          setToneIntensity(data.profile.toneIntensity || 0.5);
          setFinancialThreshold(data.profile.financialThreshold?.toString() || '');
          setNonNegotiable(data.profile.nonNegotiable || '');
          setAutonomyMode(data.profile.autonomyMode || 'advisory');
          setAlignment(data.alignment);
          setAutonomyLimits(data.autonomyLimits);
        }
      } catch (e) {
        console.error('Failed to load cognitive profile:', e);
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [replicaId, user]);

  // ── Save profile ───────────────────────────────────────────────
  const saveProfile = useCallback(async () => {
    setSaving(true);
    try {
      const authToken = await getToken(user);
      const res = await fetch(`${API_BASE}/api/v1/cognitive/${replicaId}/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          identityIntro: identityIntro || null,
          commTone: commTone || null,
          toneIntensity,
          financialThreshold: financialThreshold ? parseFloat(financialThreshold) : null,
          nonNegotiable: nonNegotiable || null,
          autonomyMode,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setExistingProfile(data.profile);
        setAlignment(data.alignment);
        setAutonomyLimits(data.autonomyLimits);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Save failed:', e);
      return false;
    } finally {
      setSaving(false);
    }
  }, [replicaId, user, identityIntro, commTone, toneIntensity, financialThreshold, nonNegotiable, autonomyMode]);

  // ── Navigation ─────────────────────────────────────────────────
  const handleNext = async () => {
    if (step < SETUP_STEPS.length - 1) {
      await saveProfile();
      setStep(step + 1);
    } else {
      const ok = await saveProfile();
      if (ok) setShowComplete(true);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
    else onBack?.();
  };

  // ── Tier color ─────────────────────────────────────────────────
  const tierColor = (tier: string) => {
    switch (tier) {
      case 'high_fidelity': return '#22c55e';
      case 'operational': return '#6366f1';
      case 'limited': return '#f59e0b';
      default: return '#64748b';
    }
  };

  const tierLabel = (tier: string) => {
    switch (tier) {
      case 'high_fidelity': return 'High Fidelity';
      case 'operational': return 'Operational';
      case 'limited': return 'Limited';
      default: return 'Guessing';
    }
  };

  // ── Loading ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Completion Screen ──────────────────────────────────────────
  if (showComplete && alignment) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-6 animate-fadeIn">
        <div className="text-center mb-10">
          <div className="text-5xl mb-4">🧠</div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Cognitive Identity Established
          </h2>
          <p className="text-slate-400 text-sm">
            {replicaName} now has a mirror of who you are. Version {existingProfile?.version || 1}.
          </p>
        </div>

        {/* Alignment Score Ring */}
        <div className="flex justify-center mb-8">
          <div className="relative w-40 h-40">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="8" />
              <circle
                cx="60" cy="60" r="52" fill="none"
                stroke={tierColor(alignment.tier)}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${alignment.score * 3.27} 327`}
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-white">{alignment.score}</span>
              <span className="text-xs text-slate-400 uppercase tracking-wider">
                {tierLabel(alignment.tier)}
              </span>
            </div>
          </div>
        </div>

        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          {[
            { label: 'Profile', value: alignment.breakdown.layer1, max: 40, icon: '🪪' },
            { label: 'Learning', value: alignment.breakdown.learning, max: 35, icon: '📚' },
            { label: 'Brain Graph', value: alignment.breakdown.brainGraph, max: 25, icon: '🧬' },
          ].map(b => (
            <div key={b.label} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 text-center">
              <div className="text-xl mb-1">{b.icon}</div>
              <div className="text-lg font-semibold text-white">{b.value}/{b.max}</div>
              <div className="text-xs text-slate-400">{b.label}</div>
            </div>
          ))}
        </div>

        {/* Autonomy Limits */}
        {autonomyLimits && (
          <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-xl p-4 mb-8">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-indigo-300">🛡️ Guardian Policy</span>
            </div>
            <p className="text-sm text-slate-300">{autonomyLimits.description}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                Max: {autonomyLimits.maxAutonomy}
              </span>
              <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                Writes: {autonomyLimits.writeActionsAllowed ? '✅ Allowed' : '❌ Blocked'}
              </span>
              <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                Financial: €{autonomyLimits.financialLimit}
              </span>
            </div>
          </div>
        )}

        <button
          onClick={() => onComplete?.()}
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl transition-colors"
        >
          Continue to Dashboard →
        </button>
      </div>
    );
  }

  // ── Step Content ───────────────────────────────────────────────
  const renderStepContent = () => {
    switch (step) {
      case 0: // Identity Intro
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              In one sentence, how should {replicaName} introduce itself when meeting someone new?
            </p>
            <textarea
              value={identityIntro}
              onChange={e => setIdentityIntro(e.target.value)}
              placeholder={`"Hi, I'm ${replicaName}'s AI representative. I handle scheduling and client communication on their behalf."`}
              className="w-full h-28 bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50"
            />
            <p className="text-xs text-slate-500">
              This becomes the opening line whenever your Replica first interacts with someone.
            </p>
          </div>
        );

      case 1: // Communication Tone
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              How does {replicaName} communicate? Pick the tone that matches your natural style.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {COMM_TONES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setCommTone(t.value)}
                  className={`p-4 rounded-xl border text-left transition-all ${
                    commTone === t.value
                      ? 'bg-indigo-900/30 border-indigo-500/50 ring-1 ring-indigo-500/30'
                      : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600/50'
                  }`}
                >
                  <div className="text-xl mb-1">{t.icon}</div>
                  <div className="text-sm font-semibold text-white">{t.label}</div>
                  <div className="text-xs text-slate-400">{t.desc}</div>
                </button>
              ))}
            </div>
            {commTone && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Subtle</span>
                  <span>Intensity: {Math.round(toneIntensity * 100)}%</span>
                  <span>Strong</span>
                </div>
                <input
                  type="range"
                  min="0" max="1" step="0.05"
                  value={toneIntensity}
                  onChange={e => setToneIntensity(parseFloat(e.target.value))}
                  className="w-full accent-indigo-500"
                />
              </div>
            )}
          </div>
        );

      case 2: // Financial Threshold
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              Above what amount should the Guardian escalate to you for approval?
              This triggers human review for any financial commitment exceeding this value.
            </p>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">€</span>
              <input
                type="number"
                value={financialThreshold}
                onChange={e => setFinancialThreshold(e.target.value)}
                placeholder="500"
                className="w-full bg-slate-800/50 border border-slate-600/50 rounded-xl pl-10 pr-4 py-3 text-white placeholder-slate-500 text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {[50, 100, 500, 1000, 5000].map(v => (
                <button
                  key={v}
                  onClick={() => setFinancialThreshold(v.toString())}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                    financialThreshold === v.toString()
                      ? 'bg-indigo-900/30 border-indigo-500/50 text-indigo-300'
                      : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:text-white'
                  }`}
                >
                  €{v.toLocaleString()}
                </button>
              ))}
            </div>
            <p className="text-xs text-amber-400/80 flex items-center gap-1">
              <span>⚠️</span> Any amount above this will require your explicit approval before your Replica can commit.
            </p>
          </div>
        );

      case 3: // Non-Negotiable
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              What's the one thing {replicaName} must <strong className="text-red-400">never</strong> do?
              This is a hard boundary that cannot be overridden.
            </p>
            <textarea
              value={nonNegotiable}
              onChange={e => setNonNegotiable(e.target.value)}
              placeholder="Example: Never share salary details with anyone outside the company..."
              className="w-full h-28 bg-slate-800/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-500/30"
            />
            <p className="text-xs text-slate-500">
              This rule is baked into your Replica's governance layer. It survives context compaction and cannot be talked around.
            </p>
          </div>
        );

      case 4: // Autonomy Mode
        return (
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              How much freedom should {replicaName} have? You can change this anytime as trust builds.
            </p>
            <div className="space-y-2">
              {AUTONOMY_MODES.map(m => (
                <button
                  key={m.value}
                  onClick={() => setAutonomyMode(m.value)}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${
                    autonomyMode === m.value
                      ? 'bg-indigo-900/30 border-indigo-500/50 ring-1 ring-indigo-500/30'
                      : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{m.icon}</span>
                    <div>
                      <div className="text-sm font-semibold text-white">{m.label}</div>
                      <div className="text-xs text-slate-400">{m.desc}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
    }
  };

  // ── Progress bar ───────────────────────────────────────────────
  const progress = ((step + 1) / SETUP_STEPS.length) * 100;

  return (
    <div className="max-w-2xl mx-auto py-8 px-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-900/30 border border-indigo-500/30 flex items-center justify-center text-xl">
            🧠
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Sovereign Core Setup</h1>
            <p className="text-xs text-slate-400">
              Human Cognitive Identity Protocol — Layer 1
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-slate-700/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-xs text-slate-400 font-mono">
            {step + 1}/{SETUP_STEPS.length}
          </span>
        </div>
      </div>

      {/* Step Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white">{SETUP_STEPS[step].title}</h2>
        <p className="text-sm text-slate-400">{SETUP_STEPS[step].subtitle}</p>
      </div>

      {/* Step Content */}
      <div className="mb-8">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          className="px-5 py-2.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          ← {step === 0 ? 'Back' : 'Previous'}
        </button>
        <button
          onClick={handleNext}
          disabled={saving}
          className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2"
        >
          {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {step === SETUP_STEPS.length - 1 ? 'Complete Setup' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

export default SovereignCoreSetup;
